import { useEffect, useMemo, useState } from 'react';
import { FormField, Select, Textarea } from '../../components/index.js';

function defaultState() {
  return {
    edipn: '',
    fullName: '',
    organization: '',
    note: '',
    isApprover: false,
    status: 'approved',
  };
}

export function AddEdipnDialog({ open, busy = false, onClose, onSubmit }) {
  const [form, setForm] = useState(defaultState);
  const edipnValid = /^\d{10}$/.test(form.edipn);
  const showEdipnError = form.edipn.length > 0 && !edipnValid;

  useEffect(() => {
    if (open) {
      setForm(defaultState());
    }
  }, [open]);

  const submitDisabled = busy || !edipnValid;

  const payload = useMemo(
    () => ({
      edipn: form.edipn,
      fullName: form.fullName.trim(),
      organization: form.organization.trim(),
      note: form.note,
      isApprover: form.isApprover,
      status: form.status,
    }),
    [form],
  );

  if (!open) return null;

  return (
    <div className="site-access-modal__overlay" onMouseDown={(event) => event.target === event.currentTarget && onClose?.()}>
      <div className="site-access-modal" role="dialog" aria-modal="true" data-testid="add-edipn-dialog">
        <div className="site-access-admin__section-head">
          <h2>+ Add EDIPN</h2>
        </div>

        <form
          className="site-access-modal__form"
          onSubmit={(event) => {
            event.preventDefault();
            if (!submitDisabled) onSubmit?.(payload);
          }}
        >
          <FormField
            label="EDIPN"
            htmlFor="site-access-add-edipn"
            required
            error={showEdipnError ? 'EDIPN must be exactly 10 digits.' : undefined}
          >
            <input
              id="site-access-add-edipn"
              className="form-input"
              data-testid="add-edipn-input"
              value={form.edipn}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  edipn: event.target.value.replace(/\D+/g, ''),
                }))
              }
              placeholder="1234567890"
              maxLength={10}
            />
          </FormField>

          <FormField label="Full name" htmlFor="site-access-add-name">
            <input
              id="site-access-add-name"
              className="form-input"
              value={form.fullName}
              onChange={(event) =>
                setForm((current) => ({ ...current, fullName: event.target.value }))
              }
              placeholder="Taylor Lee"
            />
          </FormField>

          <FormField label="Organization" htmlFor="site-access-add-org">
            <input
              id="site-access-add-org"
              className="form-input"
              value={form.organization}
              onChange={(event) =>
                setForm((current) => ({ ...current, organization: event.target.value }))
              }
              placeholder="DHA Support"
            />
          </FormField>

          <FormField label="Starting state" htmlFor="site-access-add-status">
            <Select
              id="site-access-add-status"
              value={form.status}
              onChange={(value) => setForm((current) => ({ ...current, status: value }))}
              options={[
                { value: 'approved', label: 'Approved' },
                { value: 'pending', label: 'Pending' },
              ]}
            />
          </FormField>

          <label className="site-access-modal__checkbox">
            <input
              type="checkbox"
              checked={form.isApprover}
              onChange={(event) => setForm((current) => ({ ...current, isApprover: event.target.checked }))}
            />
            <span>Can approve site access</span>
          </label>

          <FormField label="Decision note" htmlFor="site-access-add-note">
            <Textarea
              id="site-access-add-note"
              value={form.note}
              onChange={(value) => setForm((current) => ({ ...current, note: value }))}
              placeholder="Optional note recorded with this add action."
            />
          </FormField>

          <div className="site-access-modal__actions">
            <button type="button" className="btn ghost" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn secondary"
              data-testid="add-edipn-submit"
              disabled={submitDisabled}
            >
              {busy ? 'Saving…' : 'Add EDIPN'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
